import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useFormContext, useWatch } from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation } from 'urql';
import { queueEventListReset } from '@/client/exchanges/cacheExchange/eventListResetQueue';
import type { EventGenerateFormValues } from '@/components/Event/EventGenerate.types';
import GeneratedSchedulePreview, {
  useGeneratedSchedulePreview,
} from '@/components/Event/GeneratedGames/GeneratedSchedulePreview';
import Box from '@/components/Unistyles/Box';
import WelcomeStepFrame from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeSecondaryAction,
} from '@/components/Welcome/WelcomeUI';
import { graphql } from '@/gql/graphql';
import generateGames from '@/utils/generateGames';

const GENERATED_SCHEDULE_FIELD_NAME = 'games';

const InsertGeneratedGames = graphql(`
  mutation InsertGeneratedGames($games: [event_insert_input!]!) {
    insert_event(objects: $games) {
      returning {
        id
      }
    }
  }
`);

function EventGenerateRegenerateAction() {
  const {
    actions: { replaceGames },
  } = useGeneratedSchedulePreview();
  const { control } = useFormContext<EventGenerateFormValues>();
  const { eventId } = useLocalSearchParams<{
    eventId?: string;
  }>();
  const selectedTeams = useWatch({
    control,
    name: 'selectedTeams',
    defaultValue: [],
  });
  const initialGames = useWatch({
    control,
    name: 'initialGames',
    defaultValue: [],
  });
  const matchupCount = useWatch({
    control,
    name: 'matchupCount',
    defaultValue: 1,
  });
  const repeatEvery = useWatch({
    control,
    name: 'repeatEvery',
    defaultValue: { weeks: 1 },
  });
  const filledSlots = initialGames
    .filter((game) => game.datetime.trim().length > 0)
    .map((game) => ({
      datetime: game.datetime.trim(),
      location: game.location?.trim() || null,
    }));
  const canRegenerateSchedule =
    matchupCount > 0 &&
    repeatEvery.weeks > 0 &&
    Boolean(eventId) &&
    selectedTeams.length > 1 &&
    filledSlots.length > 0;

  return (
    <WelcomeSecondaryAction
      label="Regenerate"
      icon="refresh-outline"
      disabled={!canRegenerateSchedule}
      onPress={() => {
        if (!canRegenerateSchedule || !eventId) {
          return;
        }

        const { games } = generateGames({
          parentId: eventId,
          teams: selectedTeams,
          initialGames: filledSlots,
          numberOfMatchups: matchupCount,
          repeatEvery,
          byeWeeks: true,
        });

        replaceGames(games);
      }}
    />
  );
}

function EventGenerateScheduleFooter({
  onPressConfirm,
}: {
  onPressConfirm(): void;
}) {
  return (
    <Box style={styles.footerContent} space="sm">
      <GeneratedSchedulePreview.RosterTray />
      <WelcomeActions>
        <GeneratedSchedulePreview.EditAction />
        <EventGenerateRegenerateAction />
        <GeneratedSchedulePreview.PrimaryAction
          label="Confirm"
          onPress={onPressConfirm}
        />
      </WelcomeActions>
    </Box>
  );
}

function EventGenerateScheduleFrame({
  onPressConfirm,
}: {
  onPressConfirm(): void;
}) {
  const {
    state: { gameIds },
  } = useGeneratedSchedulePreview();

  return (
    <WelcomeStepFrame
      title="Review game dates"
      subtitle={`${gameIds.length} games will be created for your Season. You can change these at any time later.`}
      wide
      footer={<EventGenerateScheduleFooter onPressConfirm={onPressConfirm} />}
    >
      <GeneratedSchedulePreview.Content
        datetimeTestIDPrefix="generated-game"
        invalidMessage="Use valid dates for every game before confirming."
      />
    </WelcomeStepFrame>
  );
}

export default function EventGeneratePreview() {
  const [, insertGeneratedGames] = useMutation(InsertGeneratedGames);
  const { handleSubmit } = useFormContext<EventGenerateFormValues>();
  const { push } = useRouter();
  const { eventId } = useLocalSearchParams<{
    eventId?: string;
  }>();
  const onSubmit = async (values: EventGenerateFormValues) => {
    const { error } = await insertGeneratedGames({
      games: values.games,
    });

    if (!error) {
      queueEventListReset({
        datetime: values.games[0]?.datetime,
        parentEventId: eventId,
      });

      push({
        pathname: '/event/[eventId]/generate/done',
        params: {
          eventId,
        },
      });
    }
  };
  const { control } = useFormContext<EventGenerateFormValues>();
  const selectedTeams = useWatch({
    control,
    name: 'selectedTeams',
    defaultValue: [],
  });

  return (
    <GeneratedSchedulePreview.Provider
      fieldName={GENERATED_SCHEDULE_FIELD_NAME}
      teams={selectedTeams}
    >
      <Stack.Screen options={{ title: 'Schedule Preview' }} />
      <EventGenerateScheduleFrame onPressConfirm={handleSubmit(onSubmit)} />
    </GeneratedSchedulePreview.Provider>
  );
}

const styles = StyleSheet.create({
  footerContent: {
    width: '100%',
  },
});
